import type { MappedServiceData } from "@/features/flights/components/flight-purchase/AdditionalServicesSelection/types";
import type { GetFlightAvailabilityTokenFlightV2 } from "@/types/codegen";
import {
  SUPPORTED_BAGGAGE_ADDITIONAL_SERVICES_CODES,
  SUPPORTED_MEAL_ADDITIONAL_SERVICES_CODES,
} from "@/utils/constants";

export const getCityNameByAirportCode = (
  cityCode?: string,
  flights?: GetFlightAvailabilityTokenFlightV2[],
) => {
  const segment = flights
    ?.flatMap((f) => f.segments)
    .find(
      (s) =>
        s.departure.airport.code === cityCode ||
        s.arrival.airport.code === cityCode,
    );

  const cityName =
    segment?.departure.airport.code === cityCode
      ? segment?.departure.city.name
      : segment?.arrival.airport.code === cityCode
        ? segment?.arrival.city.name
        : undefined;

  return cityName || cityCode;
};

export const mapServicesToRoutes = (
  services: MappedServiceData[],
  allowedServicesCode?: string[],
) => {
  const filteredServices = allowedServicesCode
    ? services.filter((s) =>
        allowedServicesCode.includes(s.service.service.categoryCode),
      )
    : services;
  return filteredServices.reduce(
    (acc, serviceData) => {
      const routeKey = `${serviceData.service.route.departureCode} - ${serviceData.service.route.arrivalCode}`;
      if (!acc[routeKey]) {
        acc[routeKey] = [];
      }
      acc[routeKey].push(serviceData);
      return acc;
    },
    {} as Record<string, MappedServiceData[]>,
  );
};

export const getRoundTripSegmentDirection = (
  departureFlight: GetFlightAvailabilityTokenFlightV2,
  returnFlight: GetFlightAvailabilityTokenFlightV2 | undefined,
  segmentDepartureCityCode: string,
  segmentArrivalCityCode: string,
): "departure" | "return" | undefined => {
  if (!returnFlight) return "departure";

  const matches = (
    segment: GetFlightAvailabilityTokenFlightV2["segments"][number],
  ) =>
    segment.departure.airport.code === segmentDepartureCityCode &&
    segment.arrival.airport.code === segmentArrivalCityCode;

  if (departureFlight.segments.some(matches)) return "departure";
  if (returnFlight.segments.some(matches)) return "return";

  return undefined;
};

export function hasOnlyOneTypeOfAdditionalServices(
  services: MappedServiceData[],
): boolean {
  const codes = services.map((s) => s.service.service.categoryCode);

  const hasBaggage = codes.some((code) =>
    SUPPORTED_BAGGAGE_ADDITIONAL_SERVICES_CODES.includes(code),
  );

  const hasMeal = codes.some((code) =>
    SUPPORTED_MEAL_ADDITIONAL_SERVICES_CODES.includes(code),
  );

  // true only if exactly one of them is present
  return (hasBaggage && !hasMeal) || (!hasBaggage && hasMeal);
}
